/**
 * pp-mobile-search-sheet — modal full-screen mobile-only para búsqueda.
 *
 * Fetcheo el mismo endpoint que pp-header-search (section pp-predictive-search)
 * para que las cards de resultado tengan la MISMA info rica (SKU, MXN, units
 * por caja, etc) que el search del header. El section devuelve HTML;
 * extraemos [data-pp-live-content] y lo inyectamos en el body del modal.
 *
 * Empty state: server-rendered cards (Productos Sugeridos + Nuestras
 * Colecciones), no requiere JS.
 *
 * Trigger: cualquier [data-pp-mobile-search-trigger] (tab bar Buscar).
 * Cierre: backdrop, close button, ESC.
 * Submit (Enter): navega a /search?q=... para resultados completos.
 */

(function () {
  const SUGGEST_URL = '/search/suggest';
  const SECTION_ID = 'pp-predictive-search';
  const DEBOUNCE_MS = 180;
  const MIN_QUERY_LEN = 2;

  let sheet, panel, input, clearBtn, body, empty, results,
      resultsContainer, loading;
  let activeController = null;
  let debounceTimer = null;

  function init() {
    sheet = document.getElementById('PpMobileSearchSheet');
    if (!sheet) return;

    panel = sheet.querySelector('[data-pp-mobile-search-panel]');
    input = sheet.querySelector('[data-pp-mobile-search-input]');
    clearBtn = sheet.querySelector('[data-pp-mobile-search-clear]');
    body = sheet.querySelector('[data-pp-mobile-search-body]');
    empty = sheet.querySelector('[data-pp-mobile-search-empty]');
    results = sheet.querySelector('[data-pp-mobile-search-results]');
    resultsContainer = sheet.querySelector('[data-pp-mobile-search-results-container]');
    loading = sheet.querySelector('[data-pp-mobile-search-loading]');

    bindTriggers();
    bindCloseHandlers();
    bindInput();
  }

  function bindTriggers() {
    document.querySelectorAll('[data-pp-mobile-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });
  }

  function bindCloseHandlers() {
    sheet.querySelectorAll('[data-pp-mobile-search-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !sheet.hasAttribute('hidden')) {
        close();
      }
    });
  }

  function bindInput() {
    if (!input) return;

    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (clearBtn) {
        clearBtn.toggleAttribute('hidden', q.length === 0);
      }

      if (debounceTimer) clearTimeout(debounceTimer);

      if (q.length < MIN_QUERY_LEN) {
        showEmpty();
        return;
      }

      debounceTimer = setTimeout(() => {
        runSearch(q);
      }, DEBOUNCE_MS);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        input.value = '';
        input.focus();
        clearBtn.setAttribute('hidden', '');
        showEmpty();
      });
    }
  }

  function open() {
    sheet.removeAttribute('hidden');
    sheet.setAttribute('aria-hidden', 'false');
    void sheet.offsetWidth;
    sheet.classList.add('is-open');
    document.body.classList.add('pp-mobile-search-open');
    setTimeout(() => {
      if (input) input.focus();
    }, 50);
  }

  function close() {
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pp-mobile-search-open');
    setTimeout(() => {
      sheet.setAttribute('hidden', '');
    }, 260);
  }

  function showEmpty() {
    if (empty) empty.removeAttribute('hidden');
    if (results) results.setAttribute('hidden', '');
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  }

  function showResults() {
    if (empty) empty.setAttribute('hidden', '');
    if (results) results.removeAttribute('hidden');
  }

  /**
   * Fetch predictive search del section pp-predictive-search (mismo
   * endpoint que el header search). Devuelve HTML; extraemos
   * [data-pp-live-content] e inyectamos en el container.
   *
   * Limpiamos resultsContainer antes de set innerHTML — el HTML
   * inyectado viene del propio servidor Shopify renderizando una
   * section trusted, no de user input, así que es safe.
   */
  function runSearch(query) {
    if (activeController) activeController.abort();

    const params = new URLSearchParams({
      q: query,
      section_id: SECTION_ID,
      'resources[type]': 'product,page,article',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,tag,vendor',
    });
    const url = SUGGEST_URL + '?' + params.toString();

    showResults();
    if (loading) loading.removeAttribute('hidden');

    const controller = new AbortController();
    activeController = controller;

    fetch(url, { signal: controller.signal })
      .then((res) => res.text())
      .then((html) => {
        if (controller.signal.aborted) return;
        injectResults(html);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[pp-mobile-search-sheet] Fetch failed:', err);
      })
      .finally(() => {
        if (loading) loading.setAttribute('hidden', '');
        if (activeController === controller) activeController = null;
      });
  }

  /**
   * Inyecta el HTML del section response. El section renderiza la
   * estructura completa pp-search__live-content con resultados o
   * empty state — solo extraemos ese nodo. NO user-input es injected;
   * el HTML viene del propio server renderizando una section trusted.
   */
  function injectResults(html) {
    if (!resultsContainer) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-pp-live-content]');

    // Clear previous results
    while (resultsContainer.firstChild) {
      resultsContainer.removeChild(resultsContainer.firstChild);
    }

    if (content) {
      // Importar el nodo entero (con todos sus children) al document actual
      const imported = document.importNode(content, true);
      resultsContainer.appendChild(imported);
    } else {
      // Section devolvió algo inesperado — mostrar mensaje neutro
      const fallback = document.createElement('p');
      fallback.className = 'pp-mss-loading';
      fallback.textContent = 'Sin resultados disponibles ahora.';
      resultsContainer.appendChild(fallback);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
